export interface UseKftLocalStorageOptions<T> {
  /** Which Web Storage API to use. Default: `local` (persists across browser sessions). */
  storage?: 'local' | 'session'
  /** Controls what is written to storage; defaults to the full state value. */
  getPersistableSnapshot?: (value: T) => unknown
}

const getWebStorage = (kind: 'local' | 'session'): Storage | undefined => {
  if (!import.meta.client || typeof window === 'undefined') {
    return undefined
  }
  return kind === 'session' ? window.sessionStorage : window.localStorage
}

export const useKftLocalStorage = <T>(
  key: string,
  defaultValue: T,
  options?: UseKftLocalStorageOptions<T>
) => {
  const storageKind = options?.storage ?? 'local'
  const webStorage = () => getWebStorage(storageKind)

  const data = useState(key, () => defaultValue)
  const loaded = ref(false)

  // Function to force load data from Web Storage
  const loadFromStorage = () => {
    if (!import.meta.client || loaded.value) return

    const store = webStorage()
    if (!store) {
      loaded.value = true
      return
    }

    let storedData
    try {
      storedData = store.getItem(key)
    } catch (error) {
      console.error('Error accessing Web Storage', error)
    }
    if (storedData) {
      data.value = JSON.parse(storedData)
    }
    loaded.value = true
  }

  onBeforeMount(() => {
    loadFromStorage()
  })

  // flush: 'sync' so writes run before the same tick continues (e.g. onSuccess
  // navigation can unmount components and drop a 'pre'-flushed watch before it runs).
  watch(
    data,
    () => {
      const store = webStorage()
      if (!store) return
      try {
        const snapshot = options?.getPersistableSnapshot
          ? options.getPersistableSnapshot(data.value)
          : data.value
        store.setItem(key, JSON.stringify(snapshot))
      } catch (error) {
        console.error('Error accessing Web Storage', error)
      }
    },
    { deep: true, flush: 'sync' }
  )

  return { data, loaded, loadFromStorage }
}
